class ProductVariantModal extends HTMLElement {
  constructor() {
    super();
    this.overlay = this.querySelector('.product-variant-modal-overlay');
    this.modalContainer = this.querySelector('.product-variant-modal-container');
    this.modalContent = this.querySelector('.product-variant-modal-content');
    this.closeButtons = this.querySelectorAll('[data-close-modal]');
    this.swatches = this.querySelectorAll('[data-variant-swatch]');
    this.variantInput = this.querySelector('[data-variant-input]');
    this.priceDisplay = this.querySelector('[data-variant-price-display]');
    this.compareAtPriceDisplay = this.querySelector('[data-variant-compare-at-price-display]');
    this.variantTitleDisplay = this.querySelector('[data-selected-variant-title]');
    this.productForm = this.querySelector('product-form');
    this.addToCartButton = this.querySelector('[data-add-to-cart-button]');
    this.productImageContainer = this.querySelector('.aspect-square');
    this.productPicture = this.productImageContainer?.querySelector('picture');
    this.productImage = this.productImageContainer?.querySelector('img');

    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.handleModalContentClick = this.handleModalContentClick.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleSwatchClick = this.handleSwatchClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  connectedCallback() {
    this.closeButtons.forEach(btn => {
      btn.addEventListener('click', this.handleClose);
    });

    this.addEventListener('click', this.handleOverlayClick);

    if (this.modalContent) {
      this.modalContent.addEventListener('click', this.handleModalContentClick);
    }

    this.swatches.forEach(swatch => {
      swatch.addEventListener('click', this.handleSwatchClick);
    });

    document.addEventListener('keydown', this.handleKeyDown);
  }

  disconnectedCallback() {
    this.closeButtons.forEach(btn => {
      btn.removeEventListener('click', this.handleClose);
    });

    this.removeEventListener('click', this.handleOverlayClick);

    if (this.modalContent) {
      this.modalContent.removeEventListener('click', this.handleModalContentClick);
    }

    this.swatches.forEach(swatch => {
      swatch.removeEventListener('click', this.handleSwatchClick);
    });

    document.removeEventListener('keydown', this.handleKeyDown);
  }

  open() {
    this.classList.remove('opacity-0', 'invisible', 'pointer-events-none');
    this.classList.add('opacity-100', 'visible');
    this.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Set initial button state based on selected variant
    if (this.variantInput && this.addToCartButton) {
      const variantId = parseInt(this.variantInput.value);
      const selectedSwatch = Array.from(this.swatches).find(s => parseInt(s.dataset.variantId) === variantId);
      if (selectedSwatch) {
        const isAvailable = selectedSwatch.dataset.variantAvailable === 'true';
        if (isAvailable) {
          this.addToCartButton.disabled = false;
          this.addToCartButton.classList.remove('opacity-50', 'cursor-not-allowed', 'pointer-events-none');
          this.addToCartButton.classList.add('hover:bg-gray-800');
          this.addToCartButton.setAttribute('aria-disabled', 'false');
          this.addToCartButton.textContent = 'ADD';
        } else {
          this.addToCartButton.disabled = true;
          this.addToCartButton.classList.add('opacity-50', 'cursor-not-allowed', 'pointer-events-none');
          this.addToCartButton.classList.remove('hover:bg-gray-800');
          this.addToCartButton.setAttribute('aria-disabled', 'true');
          this.addToCartButton.textContent = 'Sold out';
        }
      }
    }

    const firstFocusable = this.querySelector('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
    if (firstFocusable) {
      firstFocusable.focus();
    }
  }

  close() {
    this.classList.add('opacity-0', 'invisible', 'pointer-events-none');
    this.classList.remove('opacity-100', 'visible');
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  handleOverlayClick(event) {
    const clickedElement = event.target;

    if (clickedElement === this.overlay) {
      this.close();
      return;
    }

    if (clickedElement === this.modalContainer) {
      this.close();
      return;
    }

    if (this.modalContent && !this.modalContent.contains(clickedElement)) {
      this.close();
    }
  }

  handleModalContentClick(event) {
    event.stopPropagation();
  }

  handleClose() {
    this.close();
  }

  handleKeyDown(event) {
    if (event.key === 'Escape' && !this.classList.contains('pointer-events-none')) {
      this.close();
    }
  }

  handleSwatchClick(event) {
    const swatch = event.currentTarget;
    const variantId = parseInt(swatch.dataset.variantId);
    const isAvailable = swatch.dataset.variantAvailable === 'true';
    const variantTitle = swatch.dataset.variantTitle;
    const variantPrice = parseInt(swatch.dataset.variantPrice);

    this.swatches.forEach(s => {
      s.classList.remove('border-black');
      s.classList.add('border-transparent');
      s.setAttribute('aria-pressed', 'false');
    });

    swatch.classList.remove('border-transparent');
    swatch.classList.add('border-black');
    swatch.setAttribute('aria-pressed', 'true');

    if (this.variantInput) {
      this.variantInput.value = variantId;
    }

    if (this.priceDisplay) {
      const formattedPrice = swatch.dataset.variantPriceFormatted;
      if (!formattedPrice) {
        console.error('[ProductVariantModal] Missing formatted price in data attribute');
        return;
      }
      this.priceDisplay.textContent = formattedPrice;
    }

    if (this.compareAtPriceDisplay) {
      const compareAtPrice = parseInt(swatch.dataset.variantCompareAtPrice || '0');
      const compareAtPriceFormatted = swatch.dataset.variantCompareAtPriceFormatted || '';
      
      if (compareAtPrice > variantPrice && compareAtPriceFormatted) {
        this.compareAtPriceDisplay.textContent = compareAtPriceFormatted;
        this.compareAtPriceDisplay.classList.remove('hidden');
      } else {
        this.compareAtPriceDisplay.textContent = '';
        this.compareAtPriceDisplay.classList.add('hidden');
      }
    }

    if (this.variantTitleDisplay) {
      this.variantTitleDisplay.textContent = variantTitle;
    }

    if (!this.productForm) {
      return;
    }
    this.productForm.dataset.variantId = variantId;
    this.productForm.dataset.variantTitle = variantTitle;
    this.productForm.dataset.variantPrice = variantPrice;

    const formattedPrice = swatch.dataset.variantPriceFormatted;
    if (formattedPrice) {
      this.productForm.dataset.variantPriceFormatted = formattedPrice;
    }

    const swatchImageContainer = swatch.querySelector('div');
    const swatchPicture = swatchImageContainer?.querySelector('picture');
    const swatchImage = swatchImageContainer?.querySelector('img');
    
    if (!swatchImage) {
      return;
    }

    if (this.productImage) {
      this.productImage.src = swatchImage.src;
      this.productImage.srcset = swatchImage.srcset || '';
      this.productImage.alt = variantTitle || this.productImage.alt;
      
      if (swatchPicture && this.productPicture) {
        const swatchSources = Array.from(swatchPicture.querySelectorAll('source'));
        const productSources = Array.from(this.productPicture.querySelectorAll('source'));
        
        swatchSources.forEach((swatchSource, index) => {
          if (productSources[index]) {
            productSources[index].srcset = swatchSource.srcset || '';
            if (swatchSource.hasAttribute('media')) {
              productSources[index].setAttribute('media', swatchSource.getAttribute('media') || '');
            }
            if (swatchSource.hasAttribute('type')) {
              productSources[index].setAttribute('type', swatchSource.getAttribute('type') || '');
            }
          } else if (swatchSource.srcset) {
            const newSource = document.createElement('source');
            if (swatchSource.hasAttribute('media')) {
              newSource.setAttribute('media', swatchSource.getAttribute('media') || '');
            }
            if (swatchSource.hasAttribute('type')) {
              newSource.setAttribute('type', swatchSource.getAttribute('type') || '');
            }
            newSource.srcset = swatchSource.srcset;
            this.productPicture.insertBefore(newSource, this.productImage);
          }
        });
      }
      
      this.productImage.dispatchEvent(new Event('load'));
    } else if (this.productImageContainer) {
      const productImage = this.productImageContainer.querySelector('img');
      if (productImage) {
        productImage.src = swatchImage.src;
        productImage.srcset = swatchImage.srcset || '';
        productImage.alt = variantTitle || productImage.alt;
        productImage.dispatchEvent(new Event('load'));
      }
    }

    if (this.productForm) {
      this.productForm.dataset.productImage = swatchImage.src;
    }

    // Update Add to Cart button state based on variant availability
    if (this.addToCartButton) {
      if (isAvailable) {
        this.addToCartButton.disabled = false;
        this.addToCartButton.classList.remove('opacity-50', 'cursor-not-allowed', 'pointer-events-none');
        this.addToCartButton.classList.add('hover:bg-gray-800');
        this.addToCartButton.setAttribute('aria-disabled', 'false');
        this.addToCartButton.textContent = 'ADD';
      } else {
        this.addToCartButton.disabled = true;
        this.addToCartButton.classList.add('opacity-50', 'cursor-not-allowed', 'pointer-events-none');
        this.addToCartButton.classList.remove('hover:bg-gray-800');
        this.addToCartButton.setAttribute('aria-disabled', 'true');
        this.addToCartButton.textContent = 'Sold out';
      }
    }
  }
}

if (!window.customElements.get('product-variant-modal')) {
  window.customElements.define('product-variant-modal', ProductVariantModal);
}

window.openProductVariantModal = (productId) => {
  const modal = document.getElementById(`product-variant-modal-${productId}`);
  if (modal) {
    modal.open();
  }
};

